import React, {
  useCallback,
  useMemo,
  useState,
  Dispatch,
  SetStateAction,
  useEffect
} from 'react';
import styles from './lesson.module.scss';
import IconMenu from 'src/assets/icons/menu.svg?react';
import IconTrash from 'src/assets/icons/trashbin.svg?react';
import { Content, Lesson as LessonType, LessonType as TeacherLessonType } from '../../../../types';
import LessonSection from './lesson-section/LessonSection';
import { useEditUnit } from '../edit/useEditUnit';
import type { DraggableProvided, DraggableStateSnapshot } from '@src/utils/componentCasts';
import { DragDropContext, Droppable, Draggable } from '@src/utils/componentCasts';
import {
  Section as SectionSchema,
  useCreateAlternateLessonMutation,
  useCreateSectionMutation,
  useDeleteAlternateLessonMutation,
} from '@src/api/graphql-codegen';
import Spinner from '@src/components/spinner/Spinner';
import Section from './alternate-lesson/Section';
import { logger } from '../../../../utils/logger';

type Props = {
  lesson: LessonType;
  setLessonOpen: Dispatch<SetStateAction<null | string>>;
  lessonOpen: null | string;
  draggableProvided?: DraggableProvided;
  snapshot?: DraggableStateSnapshot;
  refetchUnit: () => void;
};

function Lesson(props: Props) {
  const { lesson, setLessonOpen, draggableProvided, snapshot, refetchUnit } = props;
  const [opened, setOpened] = useState(false);
  const [alternateLessionOpened, setAlternateLessonOpened] = useState(false);
  const [sections, setSections] = useState<Array<SectionSchema>>([]);

  const { setLesson, setSelectionLevel, setContent } = useEditUnit();

  const [createAlternateLesson, { loading: loading1 }] = useCreateAlternateLessonMutation({
    onCompleted(res) {
      const data = res.createAlternateLesson;
      if (data) {
        setSections(data as SectionSchema[]);
        setLesson({...lesson, sections: data});
      }
      setAlternateLessonOpened(true)
    },
    update() {
      refetchUnit()
    },
    onError(e) {
      logger.debug(`[e]`, e);
    }
  });
  const [removeAlternateLesson, { loading: loading2 }] = useDeleteAlternateLessonMutation({
    onCompleted(res) {
      const data = res.deleteAlternateLesson;
      if (data) {
        setSections([]);
        setLesson({...lesson, sections: []});
      }
      setAlternateLessonOpened(false)
    },
    update() {
      refetchUnit()
    },
    onError(e) {
      logger.debug(`[e]`, e);
    }
  });
  const [createSection, { loading: loading3 }] = useCreateSectionMutation({
    onCompleted(res) {
      const data = res.createSection;
      if (data) {
        let newSections = [...sections];
        newSections.splice(sections.length - 1, 0, data);
        setSections(newSections);
        setLesson({ ...lesson, sections: newSections });
      }
    },
    update() {
      refetchUnit()
    },
    onError(e) {
      logger.debug(`[e]`, e);
    }
  });

  const wrapperClasses = useMemo(() => {
    const classes = [styles.wrapper];

    if (opened) {
      classes.push(styles.opened);
    }

    if (snapshot?.isDragging) {
      classes.push(styles.moving);
    }

    return classes.join(' ');
  }, [opened, snapshot?.isDragging]);

  const handleSelectLesson = useCallback(() => {
    setOpened(!opened);
    if (!opened) setLessonOpen(lesson.id);
    else setLessonOpen(null);
    setSelectionLevel('Lesson');
    setLesson(lesson);
    setContent(undefined);
  }, [lesson, opened, setContent, setLesson, setLessonOpen, setSelectionLevel]);

  const handleClose = (e : React.SyntheticEvent) => {
    e.stopPropagation();
    setOpened(!opened);
    if (!opened) setLessonOpen(lesson.id);
    else setLessonOpen(null);
  };

  const contentClasses = useMemo(() => {
    const classes = [styles.content];

    return classes.join(' ');
  }, []);

  const cpres = useMemo(() => [...lesson?.modules || []], [lesson.modules]);
  const cmaterials = useMemo(() => [...lesson?.materials || []], [lesson?.materials]);

  useEffect(() => {
    if (lesson.sections) setSections(lesson.sections as SectionSchema[])
  }, [lesson])

  const handleAlternateLesson = useCallback(() => {
    setAlternateLessonOpened(!alternateLessionOpened);
  }, [alternateLessionOpened])

  const addAlternateLesson = useCallback(() => {
    if (!loading1) {
      createAlternateLesson({
        variables: {
          input: { id: lesson?.id }
        }
      });
    }
  }, [createAlternateLesson, lesson?.id, loading1])

  const deleteAlternateLesson = useCallback(() => {
    if (!loading2) {
      removeAlternateLesson({
        variables: {
          input: { id: lesson?.id }
        }
      });
    }
  }, [lesson?.id, loading2, removeAlternateLesson])

  const addNewSection = useCallback(() => {
    if (!loading2 && !loading3) {
      createSection({
        variables: {
          input: {
            lessonId: lesson?.id,
            name: 'New section'
          }
        }
      });
    }
  }, [createSection, lesson?.id, loading2, loading3])

  return (
    <div className={wrapperClasses} {...draggableProvided?.dragHandleProps}>
      <div className={styles.menu} style={{
        display: snapshot?.isDragging ? "flex" : ""
      }}>
        <IconMenu />
      </div>
      <ul className={styles.tree}>
        <li key={'lessonTreeHeader'}>
          <section onClick={handleSelectLesson} className={`${styles.header}`}>
            <div className={styles.headerContent}>
              <div className={styles.mainLessonIcon}>M</div>
              <div className={styles.lessonTitle} title={lesson.name}>Lesson {lesson.number}:<div className={styles.lessonName}>&nbsp;{lesson.name}</div></div>
            </div>
            {lesson.duration ? <div className={styles.durationWrapper} onClick={handleClose}>
              <div className={styles.title}>{lesson.duration} mins</div>
            </div> : <></>}
          </section>
          <section className={contentClasses}>
            <LessonSection
              lessonOpened={opened}
              lessonType={TeacherLessonType.PRESENTATION}
              lesson={lesson}
              contents={cpres as Content[]}
              materialContents={cmaterials as Content[]}
            />
          </section>
        </li>
        <li key={'lessonTreeSection'}>
          {sections.length ?
            <>
              <section className={`${styles.header}`}>
                <div className={styles.headerContent} onClick={handleAlternateLesson}>
                  <div className={styles.alternateLessonIcon}>A</div>
                  <div className={styles.lessonTitle} title={lesson.name}>Lesson {lesson.number}:<div className={styles.lessonName}>&nbsp;{lesson.name}</div></div>
                </div>
                <div className={styles.durationWrapper}>
                  {loading2 ? <Spinner width={28} height={28} /> : <IconTrash onClick={deleteAlternateLesson}/>}
                </div>
              </section>
              <ul className={`${styles.sectionTree} ${alternateLessionOpened ? styles.sectionTreeShown : styles.sectionTreeHidden}`}>
                {sections.filter((n, i) => (i !== sections.length - 1)).map((item: SectionSchema, index: number) => {
                  return (
                    <li key={item.id}>
                      <Section
                        lessonOpened={opened}
                        lessonType={TeacherLessonType.PRESENTATION}
                        lesson={lesson}
                        section={item}
                        sectionBeingDeleted={loading2}
                        refetchUnit={refetchUnit}
                        contents={item.modules as Content[]} />
                    </li>
                  );
                })}
                <li key={'sectionTreeFooter'}>
                  <section className={`${styles.header} ${styles.newSectionHeader}`} onClick={addNewSection}>
                    <div className={styles.headerContent}>
                      <div className={styles.addAlternateLessonIcon}></div>
                      <div className={styles.lessonTitle}><div className={styles.lessonName}>Add Another Section</div></div>
                    </div>
                    {loading3 ? <div className={styles.durationWrapper}>
                      <Spinner width={17} height={17} />
                    </div> : null}
                  </section>
                </li>
                <li key={'exitTicketSection'}>
                  <Section
                    lessonOpened={opened}
                    lessonType={TeacherLessonType.PRESENTATION}
                    lesson={lesson}
                    section={sections[sections.length - 1]}
                    sectionBeingDeleted={loading2}
                    refetchUnit={refetchUnit}
                    contents={sections[sections.length - 1].modules as Content[]} />
                </li>
              </ul>
            </> :
            <section className={`${styles.header} ${styles.addAlternateLessonHeader}`} onClick={addAlternateLesson}>
              <div className={styles.headerContent}>
                <div className={styles.addAlternateLessonIcon}></div>
                <div className={styles.lessonTitle}><div className={styles.lessonName}>Add Alternate Lesson</div></div>
              </div>
              {loading1 ? <div className={styles.durationWrapper}>
                <Spinner width={20} height={20} />
              </div> : null}
            </section>
          }
        </li>
      </ul>
    </div>
  );
}

export default Lesson;
